/**
 * Admin — AI Tasks Global Stats — FASE 6
 * /admin/ops/ai/tasks
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2, Zap, CheckCircle2, XCircle, BarChart2, Clock } from "lucide-react";

export const Route = createFileRoute("/admin/ops/ai/tasks")({
  component: AdminTasksPage,
  head: () => ({ meta: [{ title: "Acciones IA — Admin — Structura" }] }),
});

interface GlobalTaskStats {
  days: number;
  byTenant: Array<{ tenant_id: string; total: number; executed: number; rejected: number }>;
  topTasks: Array<{ task_id: string; count: number }>;
  events: {
    proposals: number;
    approved: number;
    rejected: number;
    completed: number;
    failed: number;
  };
  avgSeconds: number;
}

function KpiCard({ label, value, icon: Icon, className }: {
  label: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  className?: string;
}) {
  return (
    <div className={`rounded-xl border bg-card p-4 flex items-start gap-3 ${className ?? ""}`}>
      <Icon className="h-5 w-5 mt-0.5 text-muted-foreground shrink-0" />
      <div>
        <p className="text-2xl font-bold tabular-nums">{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}

function AdminTasksPage() {
  const [stats, setStats] = useState<GlobalTaskStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void fetch("/api/admin/ai/tasks?days=30", { credentials: "include" })
      .then((r) => r.json())
      .then((d: { stats: GlobalTaskStats }) => setStats(d.stats))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!stats) {
    return <div className="p-6 text-sm text-muted-foreground">Sin datos disponibles.</div>;
  }

  const ev = stats.events ?? {};
  const approvalRate = ev.proposals > 0
    ? Math.round((ev.approved / ev.proposals) * 100)
    : 0;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-base font-semibold flex items-center gap-2">
          <Zap className="h-4 w-4 text-violet-500" />
          Task Orchestrator — Métricas globales
        </h2>
        <p className="text-sm text-muted-foreground">Últimos {stats.days} días · Todos los tenants</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiCard label="Propuestas generadas" value={ev.proposals ?? 0} icon={Zap} />
        <KpiCard label="Aprobadas" value={ev.approved ?? 0} icon={CheckCircle2} />
        <KpiCard label="Rechazadas" value={ev.rejected ?? 0} icon={XCircle} />
        <KpiCard
          label="Tasa aprobación"
          value={`${approvalRate}%`}
          icon={BarChart2}
          className={approvalRate >= 50 ? "border-emerald-200" : "border-amber-200"}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <KpiCard label="Completadas" value={ev.completed ?? 0} icon={CheckCircle2} />
        <KpiCard
          label="Tiempo promedio"
          value={stats.avgSeconds > 0 ? `${Math.round(stats.avgSeconds)}s` : "—"}
          icon={Clock}
        />
      </div>

      {/* Top tasks */}
      {stats.topTasks.length > 0 && (
        <div>
          <h3 className="text-sm font-medium mb-2">Top tareas más usadas</h3>
          <div className="rounded-xl border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-4 py-2 text-xs text-muted-foreground font-medium">Tarea</th>
                  <th className="text-right px-4 py-2 text-xs text-muted-foreground font-medium">Usos</th>
                </tr>
              </thead>
              <tbody>
                {stats.topTasks.map((t, i) => (
                  <tr key={t.task_id} className={i % 2 === 0 ? "" : "bg-muted/20"}>
                    <td className="px-4 py-2 font-mono text-xs">{t.task_id}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{t.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* By tenant */}
      {stats.byTenant.length > 0 && (
        <div>
          <h3 className="text-sm font-medium mb-2">Por tenant</h3>
          <div className="rounded-xl border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-4 py-2 text-xs text-muted-foreground font-medium">Tenant</th>
                  <th className="text-right px-4 py-2 text-xs text-muted-foreground font-medium">Total</th>
                  <th className="text-right px-4 py-2 text-xs text-muted-foreground font-medium">Ejec.</th>
                  <th className="text-right px-4 py-2 text-xs text-muted-foreground font-medium">Rech.</th>
                </tr>
              </thead>
              <tbody>
                {stats.byTenant.map((t, i) => (
                  <tr key={t.tenant_id} className={i % 2 === 0 ? "" : "bg-muted/20"}>
                    <td className="px-4 py-2 font-mono text-xs truncate max-w-[160px]">{t.tenant_id}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{t.total}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-emerald-600">{t.executed}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-red-500">{t.rejected}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
